// Third Party
import React from 'react';
import classNames from 'classnames';

// Components
import ComparisonBaseCell from '../ComparisonTable/ComparisonBaseCell';
import { CurrencyCell, PercentageCell } from '../../../components/cells';
import { IconButton } from '../../../components/buttons';
import { HoldingIcon } from '../../../components/common';
import { UnitInput } from '../../../components/inputs';
import IncomeStreamScenarioInfo from './IncomeStreamScenarioInfo';
import { Tooltip, DisclaimerTooltip } from '../../../components/tooltips';
import ComparisonCardHoldingIcon from '../ComparisonTable/ComparisonCardHoldingIcon';
import ComparisonRiskProfileLabel from '../misc/ComparisonRiskProfileLabel';
import { CurrencyLabel } from '@/views/components/misc';

// Utils
import { FormattedMessage, formatMessage, formatLocalizedString } from '../../../../utils/i18n';
import { COMPARISON_ITEM_TYPES } from '../../../../utils/constants';
import ComparisonCardMenu from '../ComparisonTable/ComparisonCardMenu';
import { userHasPermission, PERMISSIONS, userHasPermissionEnabled } from '../../../../utils/permissions';
import { getShortHistoryDatesForItem, getFieldValueDisclaimer } from '../../../../utils/disclaimers/comparison';
import { DISCLAIMER_KEYS } from '../../../../utils/disclaimers/constants';
import Comparison from '@/types/comparison';
import IncomeStreamScenario from '@/types/income-stream-scenario';
import ComparisonItem from '@/types/comparison-item';
import Nullable from '@/types/nullable';
import User from '@/types/user';

type Props = {
  className?: string;
  comparison: Comparison;
  scenario?: IncomeStreamScenario;
  item: ComparisonItem;
  years: string[];
  isBaseScenario?: boolean;
  scenarioNumber?: number;
  readOnly?: boolean;
  user?: Nullable<User>;
  isEdited?: boolean;
  handleChangeCapital: (capital: number) => void;
  handleRemoveScenario: (scenario?: IncomeStreamScenario) => void;
  handleAddScenario: (item: ComparisonItem) => void;
  loadScenarioForEdit: (scenario: IncomeStreamScenario) => void;
  onItemRemove: (item: ComparisonItem) => void;
  onModifyPortfolio: (item: ComparisonItem) => void;
  onClonePortfolio: (item: ComparisonItem) => void;
  onSavePortfolio: (item: ComparisonItem, toContact?: boolean) => void;
  updateComparisonItemColor: (item: ComparisonItem, color: string) => void;
  popoverContainerRef: Nullable<HTMLDivElement>;
  scrollbarContainerRef?: HTMLElement;
};

const NAV_FILTER = 'nav';

class IncomeStreamComparisonScenario extends React.Component<Props> {
  static defaultProps = {
    handleChangeCapital: () => {},
    handleRemoveScenario: () => {},
    handleAddScenario: () => {},
    loadScenarioForEdit: () => {},
    onItemRemove: () => {},
    onModifyPortfolio: () => {},
    onClonePortfolio: () => {},
    onSavePortfolio: () => {},
  };

  // These should be in order of priority -- first disclaimer that exists on the item will be displayed.
  HEADER_DISCLAIMERS = {
    [COMPARISON_ITEM_TYPES.FUND]: [DISCLAIMER_KEYS.FUND_NO_NAV_DATA, DISCLAIMER_KEYS.FUND_NAV_CLOSED],
    [COMPARISON_ITEM_TYPES.STOCK]: [DISCLAIMER_KEYS.STOCK_NO_NAV_DATA, DISCLAIMER_KEYS.STOCK_NAV_CLOSED],
    [COMPARISON_ITEM_TYPES.PORTFOLIO]: [DISCLAIMER_KEYS.PORTFOLIO_CLOSED_HOLDINGS_NAV],
    [COMPARISON_ITEM_TYPES.BENCHMARK]: [],
    [COMPARISON_ITEM_TYPES.GROUP]: [],
    [COMPARISON_ITEM_TYPES.ALT]: [],
  };

  renderResults = () => {
    const { scenario, years, isBaseScenario, item, comparison } = this.props;

    const fields = [
      { i18n: 'Comparison.IncomeStream.Reinvested', hidden: !isBaseScenario },
      { i18n: 'Comparison.IncomeStream.ValueWithDist', hidden: isBaseScenario },
      { i18n: 'Comparison.IncomeStream.Distributions' },
    ].filter(f => !f.hidden);

    return (
      <>
        {this.renderSectionTitles(fields)}

        {years.map(year => {
          const capital = scenario?.data?.results?.[year]?.capital;
          const average = scenario?.data?.results?.[year]?.average;
          const disclaimer = getFieldValueDisclaimer(comparison, item, `nav_${year}`);

          return (
            <div className="data-row d-flex" key={`${scenario && scenario.id}-${year}`}>
              <ComparisonBaseCell>
                <CurrencyCell value={{ value: capital }} valueDisclaimer={disclaimer} />
              </ComparisonBaseCell>

              <ComparisonBaseCell>
                <CurrencyCell value={{ value: average }} />
              </ComparisonBaseCell>
            </div>
          );
        })}
      </>
    );
  };

  getCardHeaderDisclaimer = () => {
    const { item } = this.props;
    const { disclaimers } = item;
    if (!disclaimers) {
      return null;
    }

    const disclaimerKey = this.HEADER_DISCLAIMERS[item.type].find(key => !!disclaimers[key]);
    if (!disclaimerKey) {
      return null;
    }

    return (
      <div className="comparison-card-disclaimer">
        <DisclaimerTooltip showLabel disclaimer={disclaimers[disclaimerKey]} data-private data-dd-privacy="mask" tooltipPlace="right" />
      </div>
    );
  };

  renderDistributionSources = () => {
    const { scenario, years } = this.props;

    const fields = [{ i18n: 'Comparison.IncomeStream.Principal' }, { i18n: 'Comparison.IncomeStream.Distribution' }];

    return (
      <>
        {this.renderSectionTitles(fields, 'distribution-source')}

        {years.map(year => {
          const gainPercent = scenario?.data?.results?.[year] && scenario.data.results[year].average > 0 ? scenario.data.results[year].gainPercent : null;
          const distributionPercent =
            scenario?.data?.results?.[year] && scenario.data.results[year].average > 0 ? scenario.data.results[year].distributionPercent : null;

          return (
            <div className="data-row d-flex" key={`${scenario && scenario.id}-${year}`}>
              <ComparisonBaseCell>
                <PercentageCell value={gainPercent} />
              </ComparisonBaseCell>

              <ComparisonBaseCell>
                <PercentageCell value={distributionPercent} />
              </ComparisonBaseCell>
            </div>
          );
        })}
      </>
    );
  };

  renderSectionTitles = (fields: { i18n: string; hidden?: boolean }[], className?: string) => (
    <div className={classNames('section-titles d-flex', className)}>
      {fields.map(f => (
        <ComparisonBaseCell key={f.i18n}>
          <FormattedMessage id={f.i18n} />
        </ComparisonBaseCell>
      ))}
    </div>
  );

  renderHoldingIcon = () => {
    const { item, popoverContainerRef, scrollbarContainerRef } = this.props;

    return (
      <ComparisonCardHoldingIcon
        item={item}
        updateComparisonItemColor={this.props.updateComparisonItemColor}
        popoverContainerRef={popoverContainerRef}
        scrollbarContainerRef={scrollbarContainerRef}
      />
    );
  };

  getRiskProfilePill = () => {
    const { comparison, item } = this.props;

    const profileToShow = comparison.riskProfile || item.riskProfile;

    if (profileToShow === undefined) {
      return null;
    }

    return <ComparisonRiskProfileLabel profile={profileToShow} isComparisonRiskProfile={comparison.riskProfile !== undefined} />;
  };

  renderHeader = () => {
    const { item, scenario, isBaseScenario, scenarioNumber, readOnly, isEdited, comparison, user } = this.props;
    const itemName = formatLocalizedString(item.name);

    const shortHistoryDates = getShortHistoryDatesForItem(item, undefined, NAV_FILTER);
    const showLongerHistory = comparison.shortHistory && shortHistoryDates.length > 0;
    const showCurrency = comparison.showBaseCurrencies && userHasPermissionEnabled(user, PERMISSIONS.CURRENCY_CONVERSIONS);

    return (
      <>
        <div className="top-row">
          <div>
            {item.holdings.length > 0 && (
              <div className="income-stream-header-title d-flex align-items-center">
                {isBaseScenario ? (
                  <>
                    {this.renderHoldingIcon()}
                    <span className="item-symbols">
                      {item.holdings
                        .map(h => h.symbol)
                        .filter(s => s != null)
                        .join(', ')}
                    </span>

                    {this.getRiskProfilePill()}
                  </>
                ) : (
                  <>
                    <HoldingIcon type={item.type} color={item.color} />
                    <FormattedMessage id="Comparison.IncomeStream.Scenario" values={{ key: scenarioNumber }} />
                  </>
                )}
              </div>
            )}

            {isBaseScenario && (
              <div className="income-stream-name">
                <Tooltip message={itemName} delayShow={300} data-private data-dd-privacy="mask">
                  <span>{itemName}</span>
                </Tooltip>

                <div className="income-stream-labels">{showCurrency && item.currency && <CurrencyLabel currency={item.currency} />}</div>
              </div>
            )}
          </div>

          {!readOnly && !isEdited && this.renderActionButtons()}
        </div>

        {!showLongerHistory && this.getCardHeaderDisclaimer()}

        {showLongerHistory && (
          <div className="comparison-card-disclaimer">
            <Tooltip
              title={formatMessage('Comparison.LongHistoryTooltip.Title')}
              message={formatMessage('Comparison.LongHistoryTooltip.Message')}
              list={shortHistoryDates}
              place="right"
            >
              <span>
                <i className="icon-info" />
                <FormattedMessage id="Comparison.ShowingLongerHistory" />
              </span>
            </Tooltip>
          </div>
        )}

        {isBaseScenario && (
          <div className="capital">
            <FormattedMessage id="Comparison.IncomeStream.InitialInvestment" />

            <UnitInput
              name="capital"
              compact
              value={(scenario && scenario.capital) || ''}
              type="number"
              min={0}
              onValueChange={capital => this.props.handleChangeCapital(Number(capital))}
              unitSymbol="$"
              disabled={isEdited || readOnly}
            />
          </div>
        )}
      </>
    );
  };

  onLoadScenario = () => {
    const { scenario } = this.props;

    if (!scenario) {
      return;
    }

    this.props.loadScenarioForEdit(scenario);
  };

  renderActionButtons = () => {
    const { isBaseScenario, item, scenario, readOnly, user, scrollbarContainerRef } = this.props;
    const showEditableActions = !!user && userHasPermission(user, PERMISSIONS.INCOME_STREAM);

    if (!isBaseScenario) {
      return (
        <>
          {showEditableActions && (
            <IconButton
              icon="pencil"
              onClick={this.onLoadScenario}
              tooltip={formatMessage('Comparison.IncomeStream.EditScenario')}
              tooltipPlace="top"
              tooltipEffect="solid"
            />
          )}

          <IconButton
            icon="ds-delete"
            onClick={() => this.props.handleRemoveScenario(scenario)}
            tooltip={formatMessage('Comparison.IncomeStream.DeleteScenario')}
            tooltipPlace="top"
            tooltipEffect="solid"
          />
        </>
      );
    }

    return (
      <>
        {showEditableActions && (
          <IconButton
            icon="plus-med"
            onClick={() => this.props.handleAddScenario(item)}
            tooltip={formatMessage('Comparison.IncomeStream.AddScenario')}
            tooltipPlace="top"
            tooltipEffect="solid"
          />
        )}

        {item.type === COMPARISON_ITEM_TYPES.PORTFOLIO ? (
          <span className="comparison-card-menu-wrapper">
            <ComparisonCardMenu
              item={item}
              canEdit={!item.providedBy || (!!user && user.id === item.providedBy.id)}
              onItemRemove={() => this.props.onItemRemove(item)}
              onModifyPortfolio={() => this.props.onModifyPortfolio(item)}
              onClonePortfolio={() => this.props.onClonePortfolio(item)}
              onSavePortfolio={toContact => this.props.onSavePortfolio(item, toContact)}
              readOnly={readOnly}
              disableSaving
              incomeStream
              scrollbarContainerRef={scrollbarContainerRef}
            />
          </span>
        ) : (
          <IconButton
            icon="ds-delete"
            onClick={() => this.props.onItemRemove(item)}
            tooltip={formatMessage('Comparison.IncomeStream.DeleteScenario')}
            tooltipPlace="top"
            tooltipEffect="solid"
          />
        )}
      </>
    );
  };

  render() {
    const { isBaseScenario, className, scenario } = this.props;

    return (
      <div className={classNames('income-stream-comparison-scenario', className)}>
        <div className="income-stream-card-header">
          {this.renderHeader()}

          {!isBaseScenario && scenario && <IncomeStreamScenarioInfo scenario={scenario} upperCaseDistributionType />}
        </div>

        {this.renderResults()}
        {this.renderDistributionSources()}
      </div>
    );
  }
}

export default IncomeStreamComparisonScenario;
